/**
 * Protocol product page behaviour.
 *
 * <s-gallery>: square scroll-snap gallery with thumbnail buttons. Thumbnails
 * scroll the track; swiping the track updates aria-current on the thumbnails.
 *
 * <s-bundle-picker>: stack radios plus the optional 6th-pack checkbox. Each
 * input carries server-rendered data (variant id, formatted total, supply and
 * shipping lines), so no money formatting happens here. The picker writes the
 * chosen variant into the product form's sole input[name="id"] and refreshes
 * every [data-bundle-*] target on the page (price, CTA totals, supply line).
 */

class SGallery extends HTMLElement {
  #ac;
  #timer;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.track = this.querySelector('[data-gallery-track]');
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    if (!this.track || this.thumbs.length === 0) return;

    this.thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.show(index), opts);
    });

    this.track.addEventListener('scroll', () => {
      clearTimeout(this.#timer);
      this.#timer = setTimeout(() => {
        const width = this.track.clientWidth || 1;
        this.#setActive(Math.round(Math.abs(this.track.scrollLeft) / width));
      }, 80);
    }, { ...opts, passive: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
  }

  show(index) {
    const direction = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: direction * index * this.track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
    this.#setActive(index);
  }

  #setActive(index) {
    this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', i === index ? 'true' : 'false'));
  }
}

class SBundlePicker extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('change', () => this.update(), { signal: this.#ac.signal });
    this.update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  update() {
    const radio = this.querySelector('input[data-bundle-option]:checked');
    if (!radio) return;

    const addon = this.querySelector('input[data-bundle-addon]');
    if (addon) {
      const allowed = radio.dataset.allowsAddon === 'true' && Boolean(addon.dataset.variantId);
      addon.disabled = !allowed;
      if (!allowed) addon.checked = false;
    }

    const source = addon?.checked ? addon : radio;
    const data = source.dataset;

    const form = this.closest('form');
    const idInput = form?.elements.namedItem('id');
    if (idInput instanceof HTMLInputElement && data.variantId) idInput.value = data.variantId;

    // Shopify already HTML-escapes translated strings, and the data attributes escape them again,
    // so decode any leftover entities (e.g. "&gt;") before writing plain text.
    const decoder = document.createElement('textarea');
    const decode = (value) => { decoder.innerHTML = value; return decoder.value; };
    const setText = (selector, value) => {
      if (value == null) return;
      const text = decode(value);
      document.querySelectorAll(selector).forEach((el) => { el.textContent = text; });
    };
    setText('[data-bundle-total]', data.total);
    setText('[data-bundle-supply]', data.supply);
    setText('[data-bundle-shipping]', data.shipping);
  }
}

if (!customElements.get('s-gallery')) customElements.define('s-gallery', SGallery);
if (!customElements.get('s-bundle-picker')) customElements.define('s-bundle-picker', SBundlePicker);
